/**
 * ordenMesaHelpers.ts — Lógica pura de órdenes de mesa.
 *
 * QUÉ EXPORTA: calcularTotalOrden, itemsPendientesDeEnviar,
 *   estadoMesaDesdeOrden, nombreMesa, esOrdenParaLlevar, etiquetaOrdenMesa,
 *   minutosTranscurridos, buildKdsNewOrderSegments, buildKdsReminderSegments,
 *   addItemToDraft, updateDraftQuantity, removeDraftItem, totalDraft,
 *   mergeItemsToDraft.
 * QUÉ NO DEBE HACER: No conoce React, red ni persistencia.
 */
import type { CartItem } from "@/types";
import { formatSelectionsSummary } from "@/lib/formatLineSelections";
import type {
  EstadoMesa,
  Mesa,
  OrdenMesa,
  OrdenMesaItem,
} from "../types/mesas.types";
import { ESTADOS_ORDEN_ACTIVA } from "../constants/ordenMesaEstados";

/** Total estimado de la orden (precio del snapshot × cantidad o peso). */
export const calcularTotalOrden = (items: OrdenMesaItem[]): number =>
  Math.round(
    items.reduce((acc, { cartItem }) => {
      const cantidad = cartItem.peso
        ? cartItem.peso
        : Math.max(1, cartItem.quantity || 1);
      const unitario = cartItem.peso
        ? cartItem.precioPorKilo || cartItem.precio || 0
        : cartItem.precio || 0;
      return acc + unitario * cantidad;
    }, 0),
  );

/** Ítems del borrador que aún no se enviaron a cocina. */
export const itemsPendientesDeEnviar = (
  items: OrdenMesaItem[],
): OrdenMesaItem[] => items.filter((item) => !item.enviado);

/** Convierte snapshots de la orden en ítems de carrito para Caja. */
export const itemsToCart = (items: OrdenMesaItem[]): CartItem[] =>
  items.map(({ cartItem }) => ({ ...cartItem }));

/** Estado visual de una mesa a partir de su orden activa. */
export const estadoMesaDesdeOrden = (orden: OrdenMesa | null): EstadoMesa => {
  if (!orden || !ESTADOS_ORDEN_ACTIVA.includes(orden.estado)) return "libre";
  if (orden.estado === "abierta") return "en_espera";
  return orden.estado === "por_cobrar" ? "por_cobrar" : "ocupada";
};

/** Etiqueta corta de una mesa ("Mesa 3 · Terraza"). */
export const nombreMesa = (mesa: Pick<Mesa, "numero" | "zona">): string =>
  mesa.zona ? `Mesa ${mesa.numero} · ${mesa.zona}` : `Mesa ${mesa.numero}`;

/** ¿La orden es de mostrador ("Para llevar", sin mesa)? */
export const esOrdenParaLlevar = (
  orden: Pick<OrdenMesa, "tipoEntrega" | "mesaId">,
): boolean => orden.tipoEntrega === "llevar" || !orden.mesaId;

/**
 * Etiqueta corta de una orden de mesa: "Mesa 3 · Terraza" o "Para llevar".
 * Unifica cómo se muestra la orden en Caja, Mesas, KDS y comandas.
 */
export const etiquetaOrdenMesa = (
  orden: Pick<OrdenMesa, "tipoEntrega" | "mesaId" | "mesaNumero" | "zona">,
): string =>
  esOrdenParaLlevar(orden)
    ? "Para llevar"
    : `Mesa ${orden.mesaNumero}${orden.zona ? ` · ${orden.zona}` : ""}`;

/** Minutos transcurridos desde una fecha ISO (nunca negativo). */
export const minutosTranscurridos = (iso: string): number =>
  Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));

/** Limpia un texto para que la síntesis de voz lo lea bien. */
const sanitizeSpeech = (text: string): string =>
  text.replace(/·/g, ",").replace(/\s+/g, " ").trim();

/**
 * Mesa + detalle hablado de los ítems enviados de una orden. No incluye número
 * de orden ni mesero (solo lo que la cocina necesita oír) y evita el símbolo
 * "×" para que la cantidad se pronuncie como número.
 */
const describeOrden = (orden: OrdenMesa): string => {
  const items = orden.items.filter((item) => item.enviado);
  const detalle = items
    .map(({ cartItem }) => {
      const cantidad = cartItem.peso
        ? `${cartItem.peso.toFixed(3)} kilos`
        : `${Math.max(1, cartItem.quantity)}`;
      const selections = formatSelectionsSummary(cartItem);
      return `${cantidad} ${cartItem.nombre}${selections ? `, ${selections}` : ""}`;
    })
    .join(". ");
  const resumen = items.length
    ? `${items.length} ${items.length === 1 ? "ítem" : "ítems"}: ${detalle}`
    : "sin ítems";
  return sanitizeSpeech(`${etiquetaOrdenMesa(orden)}. ${resumen}.`);
};

/** Segmentos hablados, uno por orden, para reproducir con pausas entre ellos. */
const describeOrdenes = (ordenes: OrdenMesa[]): string[] =>
  ordenes.map(describeOrden);

/**
 * Segmentos del aviso de órdenes nuevas: primero el conteo y luego cada orden.
 */
export const buildKdsNewOrderSegments = (nuevas: OrdenMesa[]): string[] => {
  if (nuevas.length === 0) return [];
  const conteo = `${nuevas.length} ${
    nuevas.length === 1 ? "orden nueva" : "órdenes nuevas"
  }`;
  return [sanitizeSpeech(`Tienes ${conteo}.`), ...describeOrdenes(nuevas)];
};

/** Etiqueta hablada del intervalo de repetición ("30 segundos", "5 minutos"). */
const formatRepeatLabel = (seconds: number): string => {
  if (seconds <= 0) return "";
  if (seconds < 60) return `${seconds} segundos`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} ${minutes === 1 ? "minuto" : "minutos"}`;
};

/**
 * Segmentos del recordatorio de órdenes pendientes: conteo, cada orden y, si
 * hay repetición configurada, el aviso de cuándo volverá a repetir.
 */
export const buildKdsReminderSegments = (
  ordenes: OrdenMesa[],
  repeatSeconds: number,
): string[] => {
  if (ordenes.length === 0) return [];
  const conteo = `${ordenes.length} ${
    ordenes.length === 1 ? "orden pendiente" : "órdenes pendientes"
  }`;
  const segments = [
    sanitizeSpeech(`Tienes ${conteo}.`),
    ...describeOrdenes(ordenes),
  ];
  if (repeatSeconds > 0) {
    segments.push(
      sanitizeSpeech(
        `Volveré a repetir en ${formatRepeatLabel(repeatSeconds)}.`,
      ),
    );
  }
  return segments;
};

// ── Mutaciones puras del borrador de carrito del mesero ─────────────────────

const mismaLinea = (a: CartItem, b: CartItem): boolean =>
  a.id === b.id &&
  (a.variantKey ?? "") === (b.variantKey ?? "") &&
  (a.modifierKey ?? "") === (b.modifierKey ?? "");

/** Agrega o incrementa una línea del borrador (sin sobrepasar el stock). */
export const addItemToDraft = (
  cart: CartItem[],
  item: CartItem,
): CartItem[] => {
  const index = cart.findIndex((line) => mismaLinea(line, item));
  if (index < 0) return [...cart, item];
  const max = Math.max(
    1,
    item.stockVariante ?? item.stock ?? Number.MAX_SAFE_INTEGER,
  );
  return cart.map((line, i) =>
    i === index
      ? { ...line, quantity: Math.min(max, Math.max(1, line.quantity + 1)) }
      : line,
  );
};

/** Cambia la cantidad de una línea del borrador respetando el stock. */
export const updateDraftQuantity = (
  cart: CartItem[],
  index: number,
  quantity: number,
): CartItem[] =>
  cart.map((line, i) => {
    if (i !== index) return line;
    const max = Math.max(
      1,
      line.stockVariante ?? line.stock ?? Number.MAX_SAFE_INTEGER,
    );
    return { ...line, quantity: Math.min(max, Math.max(1, quantity)) };
  });

/** Quita una línea del borrador. */
export const removeDraftItem = (cart: CartItem[], index: number): CartItem[] =>
  cart.filter((_, i) => i !== index);

/** Suma la cantidad de un ítem a su línea (o lo agrega) respetando el stock. */
const addItemQuantityToDraft = (
  cart: CartItem[],
  item: CartItem,
): CartItem[] => {
  // Las líneas por peso no se fusionan: cada pesaje es una línea propia.
  if (item.peso) return [...cart, item];
  const index = cart.findIndex((line) => mismaLinea(line, item));
  if (index < 0) return [...cart, item];
  return cart.map((line, i) => {
    if (i !== index) return line;
    const max = Math.max(
      1,
      item.stockVariante ?? item.stock ?? Number.MAX_SAFE_INTEGER,
    );
    const cantidad = Math.max(1, item.quantity || 1);
    return {
      ...line,
      quantity: Math.min(max, Math.max(1, line.quantity + cantidad)),
    };
  });
};

/** Fusiona una lista de ítems en el borrador sumando cantidades por línea. */
export const mergeItemsToDraft = (
  cart: CartItem[],
  items: CartItem[],
): CartItem[] =>
  items.reduce((acc, item) => addItemQuantityToDraft(acc, item), cart);

/** Total estimado del borrador (cantidad o peso). */
export const totalDraft = (cart: CartItem[]): number =>
  Math.round(
    cart.reduce((acc, item) => {
      const cantidad = item.peso ? item.peso : Math.max(1, item.quantity || 1);
      const unitario = item.peso
        ? item.precioPorKilo || item.precio || 0
        : item.precio || 0;
      return acc + unitario * cantidad;
    }, 0),
  );
